import { clsx } from 'clsx';
import { CSSProperties, forwardRef, PropsWithChildren, ReactElement, ReactNode, Ref } from 'react';
import { Checkbox as AriaCheckbox } from 'react-aria-components';
import Text from '~/components/typographies/Text';
import useDataTestId from '~/hooks/useDataTestId';
import IconCheck from '~/icons/icon-check.svg?react';
import IconMinus from '~/icons/icon-minus.svg?react';
import styles from './Checkbox.module.scss';

export interface CheckboxProps {
  'aria-label'?: string;
  checked: boolean;
  className?: string;
  errorMessage?: string;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  isInvalid?: boolean;
  onChange?: (checked: boolean) => void;
  otherInfo?: string;
  slot?: string | null;
  strong?: boolean;
  style?: CSSProperties;
  extraLabel?: ReactNode | ReactElement | string;
  dataTestId?: string;
}

const Checkbox = forwardRef(
  (
    {
      'aria-label': ariaLabel,
      checked,
      children,
      className,
      errorMessage,
      isDisabled,
      isIndeterminate,
      isInvalid,
      onChange,
      otherInfo,
      slot,
      strong,
      style,
      extraLabel,
      dataTestId,
    }: PropsWithChildren<CheckboxProps>,
    ref: Ref<HTMLDivElement>,
  ) => {
    const dt = useDataTestId(dataTestId);
    return (
      <div className={styles.outerContainer} ref={ref}>
        <AriaCheckbox
          aria-label={ariaLabel}
          className={clsx(
            styles.control,
            {
              [styles.checked!]: checked || isIndeterminate,
              [styles.strong!]: strong,
            },
            className,
          )}
          isDisabled={isDisabled}
          isInvalid={isInvalid}
          isIndeterminate={isIndeterminate}
          isSelected={checked}
          onChange={onChange}
          slot={slot}
          style={style}
          data-testid={dt()}
        >
          <div className={styles.checkboxContainer}>
            <div className={styles.checkbox}>
              {isIndeterminate && <IconMinus className={styles.icon} />}
              {!isIndeterminate && <IconCheck className={styles.icon} />}
            </div>
          </div>
          {children}
        </AriaCheckbox>
        {extraLabel && <div className={styles.extraLabel}>{extraLabel}</div>}
        {otherInfo && (
          <Text variant="caption" weight="medium" secondaryColor>
            {otherInfo}
          </Text>
        )}
        {errorMessage && (
          <Text variant="caption" weight="medium" className={styles.errorMessage}>
            {errorMessage}
          </Text>
        )}
      </div>
    );
  },
);

export default Checkbox;
